import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useGetFacetsQuery, type ExploreFilters, type LabelKey } from 'services/exploreService';
import { fmt } from '.';

export const LABEL_OPTIONS: { value: LabelKey; label: string }[] = [
  { value: 'view_label', label: 'Human view label' },
  { value: 'quality_label', label: 'Quality label' },
  { value: 'segmentation', label: 'Segmentation' },
  { value: 'keypoints', label: 'Keypoints' },
  { value: 'view_prediction', label: 'AI view prediction' },
];

interface Props {
  filters: ExploreFilters;
  update: (patch: Partial<ExploreFilters> & { page?: number }) => void;
  clear: () => void;
  activeCount: number;
  total?: number;
  unit: string;
  level: 'study' | 'dicom';
  placeholder: string;
}

const facetLabel = (label: string, n?: number) => (n != null ? `${label} (${fmt(n)})` : label);

/** One row of filters above the results; every control writes to the URL. */
const FilterBar: React.FC<Props> = ({ filters, update, clear, activeCount, total, unit, level, placeholder }) => {
  const { data: facets, isLoading } = useGetFacetsQuery();
  const [q, setQ] = useState(filters.q ?? '');
  useEffect(() => setQ(filters.q ?? ''), [filters.q]);

  const common = { allowClear: true, loading: isLoading, showSearch: true, optionFilterProp: 'label' as const, popupMatchSelectWidth: false };

  const models = (facets?.models ?? []).filter(m => !filters.manufacturer || m.manufacturer === filters.manufacturer);

  return (
    <div className="ax-filters" role="search">
      <Input
        className="grow"
        prefix={<SearchOutlined />}
        placeholder={placeholder}
        value={q}
        allowClear
        aria-label="Search"
        onChange={e => {
          setQ(e.target.value);
          if (!e.target.value) update({ q: undefined });
        }}
        onPressEnter={() => update({ q: q.trim() || undefined })}
      />
      <Select
        {...common}
        placeholder="Source"
        aria-label="Datasource"
        value={filters.datasource}
        onChange={v => update({ datasource: v })}
        options={(facets?.datasources ?? []).map(d => ({ value: d.value, label: d.label ?? d.value }))}
      />
      <Select
        {...common}
        placeholder="Manufacturer"
        aria-label="Manufacturer"
        value={filters.manufacturer}
        onChange={v => update({ manufacturer: v, model: undefined })}
        options={(facets?.manufacturers ?? []).map(d => ({ value: d.value, label: facetLabel(d.value, d.n) }))}
      />
      {level === 'dicom' && (
        <Select
          {...common}
          placeholder="Model"
          aria-label="Scanner model"
          value={filters.model}
          onChange={v => update({ model: v })}
          options={models.map(d => ({ value: d.value, label: facetLabel(d.value, d.n) }))}
        />
      )}
      <Select
        {...common}
        placeholder="View"
        aria-label="View"
        value={filters.view}
        onChange={v => update({ view: v })}
        options={(facets?.views ?? []).map(d => ({ value: d.value, label: d.value }))}
      />
      {level === 'dicom' && (
        <Select
          {...common}
          placeholder="Type"
          aria-label="DICOM type"
          value={filters.type}
          onChange={v => update({ type: v })}
          options={(facets?.types ?? []).map(d => ({ value: d.value, label: facetLabel(d.value, d.n) }))}
        />
      )}
      <Select
        {...common}
        placeholder="Year"
        aria-label="Visit year"
        style={{ minWidth: 110 }}
        value={filters.year}
        onChange={v => update({ year: v })}
        options={(facets?.years ?? []).map(d => ({ value: d.value, label: facetLabel(d.value, d.n) }))}
      />
      <Select
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="Has annotation"
        aria-label="Has annotation"
        style={{ minWidth: 190 }}
        value={filters.label ?? []}
        onChange={v => update({ label: v as LabelKey[] })}
        options={LABEL_OPTIONS}
      />
      {level === 'study' && (
        <Select
          allowClear
          placeholder="Report"
          aria-label="Has report"
          style={{ minWidth: 120 }}
          value={filters.has_report}
          onChange={v => update({ has_report: v })}
          options={[
            { value: 'true', label: 'With report' },
            { value: 'false', label: 'No report' },
          ]}
        />
      )}
      {filters.text && (
        <Tag closable color="blue" onClose={() => update({ text: undefined })} style={{ marginInlineEnd: 0 }}>
          Report mentions “{filters.text}”
        </Tag>
      )}
      {activeCount > 0 && (
        <Button type="link" onClick={clear}>
          Clear {activeCount}
        </Button>
      )}
      <span className="count" aria-live="polite">
        {total != null ? `${fmt(total)} ${unit}` : ''}
      </span>
    </div>
  );
};

export default FilterBar;
